import React, { useState, useEffect, ReactNode, TouchEvent, useRef } from "react";
import useMobile from "@/app/hooks/use-mobile";
import LoadingIcon from "@/app/assets/icons/loading";
import ReferComponent from "./refer-component";
import { X } from "lucide-react";
import StartKycCompTemp from "./start-kyc-temp";
import GoogleRecaptcha from "@/app/hooks/google-recaptcha";
import { AMZ_VOUCHER } from "@/app/utils/constants";

interface SwipeableDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
}

const RECAPTCHA_CONFIG = {
    enabled: process.env.APP_RECAPTCHA_ENABLED === 'true', // Control via env variable
    siteKey: process.env.APP_RECAPTCHA_KEY || ""
};

const SwipeableDrawer: React.FC<SwipeableDrawerProps> = ({ 
    isOpen, 
    onClose, 
    children,
}) => {
    const [startY, setStartY] = useState<number | null>(null);
    const [currentY, setCurrentY] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [shouldRender, setShouldRender] = useState<boolean>(false);
    const bodyRef = useRef<HTMLBodyElement | null>(null);
    const drawerRef = useRef<HTMLDivElement>(null);

    // Control rendering and visibility based on open state
    useEffect(() => {
        bodyRef.current = document.querySelector("body");

        if (isOpen) {
            // When opening, first render the component
            setShouldRender(true);
            // Then set visible after a tiny delay to trigger animation
            setTimeout(() => {
                setIsVisible(true);
                bodyRef.current?.classList.add("overflow-hidden");
            }, 10);
        } else {
            // When closing, first make it invisible to start animation
            setIsVisible(false);
            bodyRef.current?.classList.remove("overflow-hidden");
            // Then remove from DOM after animation completes
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, 300); // Match animation duration
            return () => clearTimeout(timer);
        }

        return () => {
            bodyRef.current?.classList.remove("overflow-hidden");
        };
    }, [isOpen]);

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>): void => {
        setStartY(e.touches[0].clientY);
        setIsDragging(true);
    };

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
        if (!startY) return;
        const currentTouch: number = e.touches[0].clientY;
        const diff: number = currentTouch - startY;
        if (diff > 0) {
            setCurrentY(diff);
        }
    };

    const handleTouchEnd = (): void => {
        setIsDragging(false);
        if (currentY > 100) {
            onClose();
        }
        setCurrentY(0);
        setStartY(null);
    };

    // Don't render anything if we shouldn't
    if (!shouldRender) return null;

    return (
        <div className="fixed inset-0 z-50">
            <div
                className={`fixed inset-0 bg-[#3C3838] bg-opacity-90 transition-opacity duration-300 ${
                    isVisible ? "opacity-100" : "opacity-0"
                }`}
                onClick={onClose}
            />
            <div
                ref={drawerRef}
                className="pt-4 px-8 fixed bottom-0 left-0 right-0 rounded-t-3xl bg-white shadow-lg transition-transform duration-300 ease-out"
                style={{
                    transform: isDragging 
                        ? `translateY(${currentY}px)` 
                        : isVisible 
                            ? 'translateY(0)' 
                            : 'translateY(100%)',
                    maxHeight: "90vh",
                    overflowY: "auto",
                }}
            >
                <div
                    className="w-full cursor-grab active:cursor-grabbing"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="h-1 w-16 mx-auto bg-gray-300 rounded-full mb-4" />
                </div>
                <div className="">{children}</div>
            </div>
        </div>
    );
};

interface ReferDrawerProps {
    isAuthenticated?: boolean;
    isKycDone?: boolean;
    userNotInvested?: boolean;
    isLoading?: boolean;
    loading: boolean;
    refLink: string;
}

const ReferDrawer: React.FC<ReferDrawerProps> = ({
    isAuthenticated, 
    isKycDone, 
    isLoading,
    loading,
    refLink
}) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const { isMobile } = useMobile();
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const hideRecaptchaBadge = () => {
          const badge = document.querySelector('.grecaptcha-badge') as HTMLElement;
          if (badge) {
            badge.style.visibility = 'hidden';
          }      
        };
      
        // Give it some time in case reCAPTCHA is not loaded instantly
        const interval = setInterval(() => {
          hideRecaptchaBadge();
        }, 0);
      
        // Clear interval once badge is found and hidden
        const timeout = setTimeout(() => {
          clearInterval(interval);
        }, 5000); // Stops trying after 5 seconds
      
        return () => {
          clearInterval(interval);
          clearTimeout(timeout);
        };
    }, []);

    if (!isMobile) return null;

    const referTabContent = (from: string) => {
        if (isLoading) {
            return (
                <div className="w-full md:w-[1152px] min-h-[423px] mx-auto flex justify-center items-center">
                    <LoadingIcon className="m-auto animate-spin w-10 h-10 sm:w-12 sm:h-12" />
                </div>
            );
        }
        if (isAuthenticated) {
            return ReferPostSection(from);
        }
    }

    const ReferPostSection = (from: string) => {
        if (loading) {
            return (
                <div className="w-full md:w-[1152px] min-h-[423px] mx-auto flex justify-center items-center">
                    <LoadingIcon className="m-auto animate-spin w-10 h-10 sm:w-12 sm:h-12" />
                </div>
            );
        }
    
        return isKycDone ? (
            RECAPTCHA_CONFIG.enabled ? (
                <GoogleRecaptcha sitekey={RECAPTCHA_CONFIG.siteKey}>
                    <ReferComponent
                        isKycDone={isKycDone}
                        refLink={refLink}
                        recaptchaEnabled={RECAPTCHA_CONFIG.enabled}
                        from={from}
                        voucherAmt={AMZ_VOUCHER}
                    />
                </GoogleRecaptcha>
            ) : (
                <ReferComponent isKycDone={isKycDone} refLink={refLink} from={from} voucherAmt={AMZ_VOUCHER} />
            )
        ) : (
            <StartKycCompTemp />
        );
    };
    
    return (
        <>
            <div className="fixed bottom-0 left-0 z-40 w-full">
                <div className="w-full bg-white px-4 py-3 shadow-md">
                    <div className="container mx-auto flex items-center justify-center">
                        <button
                            ref={buttonRef}
                            className="w-full rounded-full bg-orange-500 py-3 text-center font-medium text-white disabled:bg-orange-200"
                            onClick={() => setIsDrawerOpen(true)}
                        >
                            Refer a Friend
                        </button>
                    </div>
                </div>
            </div>

            <SwipeableDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
                <div className="text-center">
                    <div
                        className="w-full flex justify-end"
                        onClick={() => setIsDrawerOpen(false)}
                        data-testid="app-download-close-btn"
                    >
                        <X size={24} />
                    </div>
                    {referTabContent("fromDrawer")}
                </div>
            </SwipeableDrawer>
        </>
    );
};

export default ReferDrawer;